import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Box, Checkbox, TextField } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowRightRoundedIcon from "@mui/icons-material/ArrowRightRounded";
import _ from "lodash";

// หาทุกใบไม้ใต้ node
function leafIds(node) {
  if (!node) return [];
  if (!node.children || node.children.length === 0) return [node.value];
  return node.children.map((c) => leafIds(c)).flat();
}

// flatten tree เพื่อ render
function flatten(nodes, expanded, depth = 0) {
  return nodes.flatMap((node) => {
    const isExpanded = expanded.includes(node.value);
    return [
      { ...node, depth },
      ...(node.children && isExpanded ? flatten(node.children, expanded, depth + 1) : []),
    ];
  });
}

// หาทุกใบไม้
function allLeafIds(nodes) {
  let result = [];
  for (let node of nodes) {
    if (node.children && node.children.length > 0) {
      result = [...result, ...allLeafIds(node.children)];
    } else {
      result.push(node.value);
    }
  }
  return result;
}

// เก็บ parent ทั้งหมด (สำหรับ search auto expand)
function collectExpanded(nodes) {
  let result = [];
  for (let node of nodes) {
    if (node.children && node.children.length > 0) {
      result.push(node.value);
      result = [...result, ...collectExpanded(node.children)];
    }
  }
  return result;
}

// helper: หา node จาก id
function findNodeById(nodes, id) {
  for (let node of nodes) {
    if (node.value === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

export default function TreeViewTable({
  nodes,
  checked,
  setChecked,
  checkedData,
  setCheckedData,
  height = "500px",
  multi = false,
}) {
  const [userExpanded, setUserExpanded] = useState([]);
  const [autoExpanded, setAutoExpanded] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const debouncedSearch = useMemo(() => _.debounce((val) => setSearch(val), 200), []);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const deepFilter = useCallback((nodes, cb) => {
    return nodes
      .map((node) => {
        if (cb(node)) return node;
        const children = deepFilter(node.children || [], cb);
        return children.length ? { ...node, children } : null;
      })
      .filter(Boolean);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return nodes;
    return deepFilter(nodes, (n) => n.label.toUpperCase().includes(search.toUpperCase()));
  }, [nodes, search, deepFilter]);

  useEffect(() => {
    if (search) setAutoExpanded(collectExpanded(filtered));
    else setAutoExpanded([]);
  }, [search, filtered]);

  const expanded = search ? autoExpanded : userExpanded;
  const flat = useMemo(() => flatten(filtered, expanded), [filtered, expanded]);
  const leafs = useMemo(() => allLeafIds(filtered), [filtered]);

  const allChecked = multi && checked.length > 0 && checked.length === leafs.length;
  const indeterminate = multi && checked.length > 0 && checked.length < leafs.length;

  const handleToggle = useCallback(
    (node) => {
      setUserExpanded((prev) =>
        prev.includes(node.value) ? prev.filter((v) => v !== node.value) : [...prev, node.value]
      );
    },
    [setUserExpanded]
  );

  const handleCheck = useCallback(
    (node, newValue) => {
      const ids = leafIds(node);

      if (!multi) {
        // multi = false เลือกใบเดียว และไม่ให้เลือก parent
        if (node.children && node.children.length > 0) return; // parent ไม่เลือกได้
        setChecked([node.value]);
        setCheckedData([node]);
        return;
      }

      if (!node.children || node.children.length === 0) {
        // เลือกใบไม้
        const newChecked = newValue
          ? [...checked, node.value]
          : checked.filter((id) => id !== node.value);

        setChecked(newChecked);

        const newCheckedData = newValue
          ? [...checkedData, node]
          : checkedData.filter((n) => n.value !== node.value);

        setCheckedData(newCheckedData);
      } else {
        // เลือก parent -> affect children
        const remainingIds = checked.filter((id) => !ids.includes(id));
        const newChecked = newValue ? [...remainingIds, ...ids] : remainingIds;
        setChecked(newChecked);

        const leafNodes = ids.map((id) => findNodeById(nodes, id)).filter(Boolean);
        const remainingData = checkedData.filter((n) => !ids.includes(n.value));
        setCheckedData(newValue ? [...remainingData, ...leafNodes] : remainingData);
      }
    },
    [checked, checkedData, setChecked, setCheckedData, multi, nodes]
  );

  return (
    <Box>
      <TextField
        placeholder="Search..."
        fullWidth
        size="small"
        value={searchInput}
        onChange={handleSearchChange}
      />

      {multi && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={(e) => {
              if (e.target.checked) {
                setChecked(leafs);
                const leafNodes = leafs.map((id) => findNodeById(nodes, id)).filter(Boolean);
                setCheckedData(leafNodes);
              } else {
                setChecked([]);
                setCheckedData([]);
              }
            }}
          />
          <span style={{ fontSize: "0.9rem" }}>เลือกทั้งหมด</span>
        </Box>
      )}

      <Box sx={{ marginTop: "10px", height, border: "1px solid #ccc", borderRadius: "8px" }}>
        <Virtuoso
          data={flat}
          itemContent={(index, node) => {
            const ids = leafIds(node);
            const isChecked = ids.every((id) => checked.includes(id));
            const isIndeterminate = !isChecked && ids.some((id) => checked.includes(id));

            return (
              <Box
                key={`${node.value}-${search}`}
                sx={{ pl: node.depth * 2, display: "flex", alignItems: "center", cursor: "default" }}
              >
                {node.children?.length > 0 && (
                  <span
                    onClick={() => handleToggle(node)}
                    style={{ cursor: "pointer", marginRight: 5, fontSize: 20 }}
                  >
                    {expanded.includes(node.value) ? (
                      <ArrowDropDownRoundedIcon />
                    ) : (
                      <ArrowRightRoundedIcon />
                    )}
                  </span>
                )}

                <Checkbox
                  size="small"
                  checked={isChecked}
                  indeterminate={isIndeterminate}
                  disabled={!multi && node.children && node.children.length > 0}
                  onChange={() => handleCheck(node, !isChecked)}
                  sx={{ p: 0.5 }}
                />
                <span style={{ fontSize: "0.9rem" }}>{node.label}</span>
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
}
